import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Delete, ScanText } from "lucide-react";
import { wmsApi, openWmsPdf, fmtKg, fmtKg2 } from "./wmsApi";
import { WButton, WBadge, WCard } from "./WmsUi";
import WmsCamera from "./WmsCamera";
import { printPieces, getPrintTarget, PrintTargetSelector } from "./printTarget";

const KEYS = ["7", "8", "9", "4", "5", "6", "1", "2", "3", ",", "0", "back"];

export default function WmsWeighing() {
  const { lotId } = useParams();
  const navigate = useNavigate();
  const [lot, setLot] = useState(null);
  const [boot, setBoot] = useState(null);
  const [display, setDisplay] = useState("");
  const [units, setUnits] = useState(1);
  const [brand, setBrand] = useState("");
  const [autoprint, setAutoprint] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [pendingPhoto, setPendingPhoto] = useState("");

  const load = useCallback(() => {
    wmsApi.get(`/wms/lots/${lotId}`).then((r) => {
      setLot(r.data);
      setBrand((b) => b || r.data.brand_default || localStorage.getItem("itrace_last_brand") || "");
    }).catch(() => toast.error("Lotto non trovato"));
  }, [lotId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { wmsApi.get("/wms/bootstrap").then((r) => setBoot(r.data)).catch(() => {}); }, []);

  const press = (k) => {
    if (k === "back") { setDisplay((d) => d.slice(0, -1)); return; }
    if (k === ",") { if (!display.includes(",")) setDisplay((d) => (d || "0") + ","); return; }
    setDisplay((d) => (d.length < 8 ? d + k : d));
  };

  const confirm = async () => {
    const w = parseFloat(display.replace(",", "."));
    if (!w || w <= 0) { toast.error("Inserisci il peso"); return; }
    setSaving(true);
    const target = getPrintTarget();
    const tab = autoprint && (!target || target.type !== "station") ? window.open("", "_blank") : null;
    try {
      const r = await wmsApi.post(`/wms/lots/${lotId}/pieces`, { items: [{ weight_kg: w, units: units || 1, brand, photo_path: pendingPhoto }] });
      const piece = r.data.created?.[0];
      setDisplay("");
      setPendingPhoto("");
      if (brand) localStorage.setItem("itrace_last_brand", brand);
      setFlash(true);
      setTimeout(() => setFlash(false), 400);
      toast.success(`Pezzo #${piece?.number} — ${fmtKg(w)} kg${brand ? " · " + brand : ""}`);
      if (autoprint && piece) {
        const res = await printPieces([piece.id], tab);
        if (res.mode === "station") toast.success(`Etichetta inviata a "${res.name}"`);
      } else if (tab) tab.close();
      load();
    } catch (e) {
      if (tab) tab.close();
      toast.error(e.response?.data?.detail || "Errore salvataggio pezzo");
    } finally { setSaving(false); }
  };

  const analyzeLabel = async (blobs) => {
    try {
      const fd = new FormData();
      fd.append("file", blobs[0], "label.jpg");
      toast.info("L'AI sta leggendo l'etichetta…");
      const r = await wmsApi.post("/wms/ai/scan-label", fd);
      const d = r.data;
      if (d.weight_kg) setDisplay(String(d.weight_kg).replace(".", ","));
      setPendingPhoto(d.photo_path || "");
      if (d.photo_path) await wmsApi.post(`/wms/lots/${lotId}/photos`, { path: d.photo_path }).catch(() => {});
      const upd = {};
      const lo = lot?.origin || {};
      if (!Object.values(lo).some((v) => v) && Object.values(d.origin || {}).some((v) => v)) upd.origin = d.origin;
      if (!lot?.lot_supplier && d.lot) upd.lot_supplier = d.lot;
      if (!lot?.expiry_date && d.expiry_date) upd.expiry_date = d.expiry_date;
      if (Object.keys(upd).length) {
        await wmsApi.put(`/wms/lots/${lotId}`, upd);
        toast.success("Dati lotto aggiornati dall'etichetta");
      }
      toast.success(d.weight_kg ? `Etichetta letta: ${fmtKg(d.weight_kg)} kg — conferma il pezzo` : "Etichetta letta (peso non trovato: digitalo)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore lettura etichetta");
    }
  };

  if (!lot) return <p className="text-zinc-500 py-8">Caricamento...</p>;
  const brands = Array.from(new Set([...(lot.brand_default ? [lot.brand_default] : []), ...(boot?.brands || [])])).slice(0, 12);
  const lastPieces = (lot.pieces || []).slice(0, 6);
  const diff = lot.expected_kg != null ? +(lot.kg_total - lot.expected_kg).toFixed(3) : null;

  return (
    <div className={`transition-colors ${flash ? "bg-green-100" : ""}`}>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <WButton className="h-12 !px-3" onClick={() => navigate(`/wms/carichi/${lot.receipt_id}`)} data-testid="weigh-back-btn"><ArrowLeft className="w-5 h-5" /></WButton>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-['Chivo'] font-black text-xl md:text-2xl tracking-tight truncate">{lot.product_name}</h1>
            <WBadge tone="dark">{lot.category_name || "—"}</WBadge>
            <WBadge>{lot.packaging}</WBadge>
          </div>
          <p className="text-sm text-zinc-500">Lotto {lot.lot_supplier || lot.internal_lot} · {lot.receipt?.supplier_name}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Colonna sinistra: stato + brand */}
        <div className="lg:col-span-2 space-y-4">
          <WCard className="p-4 border-2 border-zinc-900">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div>
                <p className="font-['Chivo'] font-black text-3xl" data-testid="weigh-total-kg">{fmtKg2(lot.kg_total)}</p>
                <p className="text-[11px] uppercase font-bold text-zinc-500">kg pesati</p>
              </div>
              <div>
                <p className="font-['Chivo'] font-black text-3xl" data-testid="weigh-total-pieces">{lot.pieces?.length || 0}</p>
                <p className="text-[11px] uppercase font-bold text-zinc-500">pezzi</p>
              </div>
            </div>
            {lot.expected_kg != null && (
              <p className={`text-center mt-2 font-bold text-sm ${Math.abs(diff) > 0.005 ? "text-amber-700" : "text-green-700"}`} data-testid="weigh-diff">
                Bolla: {fmtKg2(lot.expected_kg)} kg → differenza {diff > 0 ? "+" : ""}{fmtKg(diff)} kg
              </p>
            )}
          </WCard>

          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500 mb-2">Brand del pezzo (tap per cambiare)</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setBrand("")} data-testid="brand-none"
                className={`h-14 px-2 font-bold uppercase text-sm border-2 truncate ${brand === "" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-300"}`}>
                — Nessuno
              </button>
              {brands.map((b) => (
                <button key={b} onClick={() => setBrand(b)} data-testid={`brand-btn-${b}`}
                  className={`h-14 px-2 font-bold uppercase text-sm border-2 truncate ${brand === b ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300"}`}>
                  {b}
                </button>
              ))}
              <button onClick={() => { const b = window.prompt("Nuovo brand:"); if (b) setBrand(b.trim()); }} data-testid="brand-new"
                className="h-14 px-2 font-bold uppercase text-sm border-2 border-dashed border-zinc-400 text-zinc-500 hover:border-zinc-900">
                + Nuovo
              </button>
            </div>
            {brand && <p className="mt-2 text-sm">Selezionato: <WBadge tone="orange">{brand}</WBadge></p>}
          </div>

          {lot.packaging === "cartone" && (
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500 mb-2">Pezzi contenuti nel collo</p>
              <div className="flex gap-2 items-center">
                {[1, 2, 4, 6, 8, 10].map((n) => (
                  <button key={n} onClick={() => setUnits(n)} data-testid={`units-btn-${n}`}
                    className={`h-12 w-12 font-black border-2 ${units === n ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-600"}`}>{n}</button>
                ))}
                <input type="number" min="1" value={units} onChange={(e) => setUnits(parseFloat(e.target.value) || 1)} className="h-12 w-20 border-2 border-zinc-300 px-2 font-bold text-center" data-testid="units-input" />
              </div>
            </div>
          )}

          <div className="hidden lg:block">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500 mb-2">Ultimi pezzi</p>
            <div className="space-y-1">
              {lastPieces.map((p) => (
                <div key={p.id} className="flex items-center gap-3 bg-white border border-zinc-200 px-3 h-12 text-sm" data-testid={`last-piece-${p.code}`}>
                  <span className="font-mono text-zinc-400">#{p.number}</span>
                  <span className="font-['Chivo'] font-black">{fmtKg(p.weight_kg)} kg</span>
                  {p.brand && <WBadge tone="orange">{p.brand}</WBadge>}
                  <span className="flex-1" />
                  <button className="h-9 px-2 border border-zinc-300 hover:border-zinc-900" onClick={() => openWmsPdf(`/wms/labels/piece/${p.id}.pdf`)} data-testid={`last-piece-print-${p.code}`}><Printer className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Numpad */}
        <div className="lg:col-span-3">
          <WButton variant="dark" className="w-full mb-2 !h-14" onClick={() => setScanOpen(true)} data-testid="weigh-scan-label-btn">
            <ScanText className="w-5 h-5" /> Scan etichetta fornitore (AI) {pendingPhoto && "· foto pronta ✓"}
          </WButton>
          <div className="bg-zinc-950 p-4 flex items-center justify-between">
            <span className="text-zinc-400 uppercase font-bold text-xs tracking-[0.2em]">Peso pezzo</span>
            <p className="font-['Chivo'] font-black text-5xl md:text-6xl text-white tabular-nums" data-testid="weigh-display">{display || "0"}<span className="text-2xl text-zinc-500 ml-2">kg</span></p>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {KEYS.map((k) => (
              <button key={k} onClick={() => press(k)} data-testid={`numpad-${k}`}
                className="h-20 md:h-24 bg-white border-2 border-zinc-200 font-['Chivo'] font-black text-4xl hover:border-zinc-900 active:translate-y-1 flex items-center justify-center">
                {k === "back" ? <Delete className="w-8 h-8" /> : k}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <button onClick={() => setDisplay("")} className="h-20 bg-white border-2 border-zinc-300 font-black uppercase text-lg text-red-600 active:translate-y-1" data-testid="numpad-clear">C</button>
            <button onClick={confirm} disabled={saving} data-testid="numpad-confirm"
              className="col-span-2 h-20 bg-[#FF4500] text-white font-['Chivo'] font-black uppercase text-2xl tracking-tight hover:bg-[#E03C00] active:translate-y-1 disabled:opacity-40">
              {saving ? "..." : "Conferma pezzo"}
            </button>
          </div>
          <label className="flex items-center gap-3 mt-3 h-14 px-4 bg-white border-2 border-zinc-300 cursor-pointer select-none" data-testid="autoprint-toggle">
            <input type="checkbox" checked={autoprint} onChange={(e) => setAutoprint(e.target.checked)} className="w-6 h-6 accent-[#FF4500]" />
            <span className="font-bold uppercase text-sm">Stampa automatica etichetta a ogni pezzo</span>
          </label>
          <PrintTargetSelector className="mt-3" />
        </div>
      </div>

      <WmsCamera open={scanOpen} onClose={() => setScanOpen(false)} onDone={analyzeLabel} title="Foto all'etichetta del fornitore" />
    </div>
  );
}
